<template>
  <div class="dv-toolbar" :class="{ 'dv-toolbar--compact': compact }" role="toolbar" aria-label="Document tools">
    <div class="dv-toolbar__group">
      <v-btn
        v-if="capabilities.sidebar"
        icon
        small
        :input-value="state.sidebarOpen"
        aria-label="Pages and outline"
        title="Pages and outline"
        @click="command('toggleSidebar')">
        <v-icon small>mdi-page-layout-sidebar-left</v-icon>
      </v-btn>

      <template v-if="capabilities.zoom">
        <v-btn icon small aria-label="Zoom out" title="Zoom out (Ctrl -)" @click="command('zoomOut')">
          <v-icon small>mdi-magnify-minus-outline</v-icon>
        </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn text small class="dv-toolbar__zoom" v-bind="attrs" aria-label="Zoom level" v-on="on">
              {{ zoomLabel }}
              <v-icon x-small right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="option in zoomOptions" :key="option.label" @click="command('setZoom', option.value)">
              <v-list-item-title>{{ option.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn icon small aria-label="Zoom in" title="Zoom in (Ctrl +)" @click="command('zoomIn')">
          <v-icon small>mdi-magnify-plus-outline</v-icon>
        </v-btn>
      </template>

      <v-btn v-if="capabilities.rotate" icon small aria-label="Rotate clockwise" title="Rotate (R)" @click="command('rotate', 90)">
        <v-icon small>mdi-rotate-right</v-icon>
      </v-btn>

      <v-btn
        v-if="capabilities.panorama"
        icon
        small
        :input-value="state.panorama"
        :aria-label="state.panorama ? 'Show as a flat image' : 'Show as a 360° panorama'"
        :title="state.panorama ? 'Show as a flat image' : 'Show as a 360° panorama'"
        @click="command('togglePanorama')">
        <v-icon small>{{ state.panorama ? 'mdi-image-outline' : 'mdi-panorama-sphere-outline' }}</v-icon>
      </v-btn>

      <v-btn
        v-if="capabilities.wrap"
        icon
        small
        :input-value="state.wrap"
        aria-label="Wrap long lines"
        title="Wrap long lines"
        @click="command('toggleWrap')">
        <v-icon small>mdi-wrap</v-icon>
      </v-btn>
    </div>

    <div v-if="capabilities.pages && state.pageCount" class="dv-toolbar__group dv-toolbar__pages">
      <v-btn icon small aria-label="Previous page" :disabled="state.page <= 1" @click="command('goToPage', state.page - 1)">
        <v-icon small>mdi-chevron-up</v-icon>
      </v-btn>
      <input
        class="dv-toolbar__page-input"
        :value="state.page"
        inputmode="numeric"
        aria-label="Page number"
        @keydown.enter="onPageInput"
        @blur="onPageInput" />
      <span class="dv-toolbar__page-count">/ {{ state.pageCount }}</span>
      <v-btn
        icon
        small
        aria-label="Next page"
        :disabled="state.page >= state.pageCount"
        @click="command('goToPage', state.page + 1)">
        <v-icon small>mdi-chevron-down</v-icon>
      </v-btn>
    </div>

    <v-spacer></v-spacer>

    <div class="dv-toolbar__group">
      <div v-if="capabilities.search && searchOpen" class="dv-toolbar__search">
        <v-icon small class="dv-toolbar__search-icon">mdi-magnify</v-icon>
        <input
          ref="searchInput"
          v-model="query"
          class="dv-toolbar__search-input"
          type="search"
          :placeholder="state.match && state.match.filter ? 'Filter rows' : 'Find in document'"
          aria-label="Find in document"
          @input="onQueryInput"
          @keydown.enter.prevent="onSearchEnter"
          @keydown.esc.stop.prevent="$emit('action', 'closeSearch')" />
        <span class="dv-toolbar__search-count" aria-live="polite">{{ matchLabel }}</span>
        <template v-if="!(state.match && state.match.filter)">
          <v-btn icon x-small aria-label="Previous match" :disabled="!hasMatches" @click="search({ previous: true })">
            <v-icon small>mdi-chevron-up</v-icon>
          </v-btn>
          <v-btn icon x-small aria-label="Next match" :disabled="!hasMatches" @click="search({})">
            <v-icon small>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-btn icon x-small aria-label="Close search" @click="$emit('action', 'closeSearch')">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-btn
        v-else-if="capabilities.search"
        icon
        small
        aria-label="Find in document"
        title="Find (Ctrl F)"
        @click="$emit('action', 'openSearch')">
        <v-icon small>mdi-magnify</v-icon>
      </v-btn>

      <span v-if="state.printing" class="dv-toolbar__progress" role="status">
        Preparing {{ state.printing.done }}/{{ state.printing.total }}
        <v-btn x-small text color="primary" @click="command('cancelPrint')">Cancel</v-btn>
      </span>

      <template v-if="!compact">
        <v-btn v-if="capabilities.print" icon small aria-label="Print" title="Print" :disabled="!!state.printing" @click="command('print')">
          <v-icon small>mdi-printer-outline</v-icon>
        </v-btn>
        <v-btn v-if="actions.download" icon small aria-label="Download" title="Download" :loading="downloading" @click="$emit('action', 'download')">
          <v-icon small>mdi-download</v-icon>
        </v-btn>
        <v-btn v-if="actions.newTab" icon small aria-label="Open in a new tab" title="Open in a new tab" @click="$emit('action', 'newTab')">
          <v-icon small>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn
          v-if="actions.fullscreen"
          icon
          small
          :aria-label="isFullscreen ? 'Exit full screen' : 'Full screen'"
          :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
          @click="$emit('action', 'fullscreen')">
          <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </v-btn>
      </template>

      <v-menu v-else offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small aria-label="More document tools" v-bind="attrs" v-on="on">
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item v-if="capabilities.print" :disabled="!!state.printing" @click="command('print')">
            <v-list-item-icon><v-icon small>mdi-printer-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Print</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="actions.download" @click="$emit('action', 'download')">
            <v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
            <v-list-item-title>Download</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="actions.newTab" @click="$emit('action', 'newTab')">
            <v-list-item-icon><v-icon small>mdi-open-in-new</v-icon></v-list-item-icon>
            <v-list-item-title>Open in a new tab</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="actions.fullscreen" @click="$emit('action', 'fullscreen')">
            <v-list-item-icon><v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon></v-list-item-icon>
            <v-list-item-title>{{ isFullscreen ? 'Exit full screen' : 'Full screen' }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script>
  const SEARCH_DEBOUNCE_MS = 250;

  /**
   * Toolbar of DocumentViewerShell. Shows only what the current renderer supports
   * (`capabilities`), and what the shell can do (`actions`). Renderer commands go out as
   * `command(name, payload)`, shell actions as `action(name)`.
   */
  export default {
    name: 'ViewerToolbar',
    props: {
      capabilities: { type: Object, default: () => ({}) },
      state: { type: Object, default: () => ({}) },
      actions: { type: Object, default: () => ({}) },
      compact: { type: Boolean, default: false },
      searchOpen: { type: Boolean, default: false },
      isFullscreen: { type: Boolean, default: false },
      downloading: { type: Boolean, default: false },
    },
    data() {
      return {
        query: '',
      };
    },
    computed: {
      zoomOptions() {
        // On a sphere there is nothing to fit; "reset" brings back the default view instead.
        if (this.state.panorama) {
          return [
            { label: 'Reset view', value: 'page-fit' },
            { label: '50%', value: 0.5 },
            { label: '100%', value: 1 },
            { label: '150%', value: 1.5 },
            { label: '200%', value: 2 },
          ];
        }
        return [
          { label: 'Fit width', value: 'page-width' },
          { label: 'Fit page', value: 'page-fit' },
          { label: '50%', value: 0.5 },
          { label: '100%', value: 1 },
          { label: '150%', value: 1.5 },
          { label: '200%', value: 2 },
          { label: '400%', value: 4 },
        ];
      },
      zoomLabel() {
        return this.state.scale ? `${Math.round(this.state.scale * 100)}%` : '100%';
      },
      hasMatches() {
        return !!this.state.match?.total;
      },
      matchLabel() {
        const match = this.state.match;
        if (!this.query || !match) return '';
        if (match.pending) return 'Searching…';
        if (!match.total) return match.filter ? 'No rows' : 'No matches';
        if (match.filter) return `${match.total.toLocaleString()} rows`;
        return `${match.current} / ${match.total}${match.capped ? '+' : ''}`;
      },
    },
    watch: {
      searchOpen(open) {
        if (open) this.$nextTick(() => this.$refs.searchInput?.focus());
        else this.query = '';
      },
    },
    created() {
      this.searchTimer = null;
    },
    beforeDestroy() {
      clearTimeout(this.searchTimer);
    },
    methods: {
      command(name, payload) {
        this.$emit('command', name, payload);
      },
      focusSearch() {
        this.$refs.searchInput?.focus();
        this.$refs.searchInput?.select();
      },
      search({ previous = false } = {}) {
        this.command('search', { query: this.query, previous, again: true });
      },
      onQueryInput() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.command('search', { query: this.query }), SEARCH_DEBOUNCE_MS);
      },
      onSearchEnter(event) {
        clearTimeout(this.searchTimer);
        this.search({ previous: event.shiftKey });
      },
      onPageInput(event) {
        const page = parseInt(event.target.value, 10);
        if (Number.isFinite(page) && page !== this.state.page) this.command('goToPage', page);
        else event.target.value = this.state.page;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .dv-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    min-height: 40px;
    padding: 2px 6px;
    overflow-x: auto;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    background: var(--v-background-base, #ffffff);
  }

  .dv-toolbar__group {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: none;
  }

  .dv-toolbar__zoom {
    min-width: 64px !important;
    padding: 0 6px !important;
    font-variant-numeric: tabular-nums;
    text-transform: none;
    letter-spacing: normal;
  }

  .dv-toolbar__pages {
    font-size: var(--ds-font-body-sm, 13px);
    font-variant-numeric: tabular-nums;
  }

  .dv-toolbar__page-input {
    width: 44px;
    height: 26px;
    padding: 0 4px;
    text-align: center;
    border: 1px solid rgba(128, 128, 128, 0.4);
    border-radius: var(--ds-radius-sm, 4px);
    color: inherit;

    &:focus {
      outline: 2px solid var(--v-primary-base);
      outline-offset: -1px;
    }
  }

  .dv-toolbar__page-count {
    margin-left: 4px;
    opacity: 0.75;
  }

  .dv-toolbar__search {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 30px;
    padding: 0 4px 0 8px;
    border: 1px solid rgba(128, 128, 128, 0.4);
    border-radius: var(--ds-radius-md, 8px);

    &:focus-within {
      border-color: var(--v-primary-base);
    }
  }

  .dv-toolbar__search-icon {
    opacity: 0.6;
  }

  .dv-toolbar__search-input {
    width: 160px;
    padding: 0 4px;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font-size: var(--ds-font-body-sm, 13px);
  }

  .dv-toolbar--compact .dv-toolbar__search-input {
    width: 110px;
  }

  .dv-toolbar__search-count {
    min-width: 56px;
    font-size: var(--ds-font-caption, 12px);
    opacity: 0.75;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .dv-toolbar__progress {
    font-size: var(--ds-font-caption, 12px);
    white-space: nowrap;
  }
</style>
